import { pricingPlansModalMessages } from '@/features/settings/billing/components/pricing-plans-modal/pricing-plans-modal.messages';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { Check } from 'lucide-react';
import { defineMessages, useIntl, type MessageDescriptor } from 'react-intl';
import { type PricingPlanCardFeature } from './pricing-plan-card-base';

const messages = defineMessages({
  customScribeTemplates: {
    defaultMessage: 'Custom scribe templates',
    id: 'yn/PUA2x83',
    description: 'Clinician plan feature on the pricing plans modal',
  },
  evidenceAnswersInSessions: {
    defaultMessage: 'Evidence answers in patient sessions',
    id: '3gl9YaxeYR',
    description: 'Clinician plan feature on the pricing plans modal',
  },
  tryAgenticWorkflows: {
    defaultMessage: 'Try agentic workflows',
    id: '4fEsSojtxK',
    description: 'Feature on Clinician and Practice pricing plan cards',
  },
  choosePreferredEvidenceSources: {
    defaultMessage: 'Choose your preferred Evidence sources',
    id: 'nZoJmGRuxn',
    description: 'Feature on Clinician and Practice pricing plan cards',
  },
  maxAgenticUsage: {
    defaultMessage: 'Maximum usage allowance for agentic workflow',
    id: 'TgJfaaJxlz',
    description: 'Clinician Max plan feature on the pricing plans modal',
  },
  designedForRegularAgenticUse: {
    defaultMessage: 'Designed for regular agentic use',
    id: 'iz4vGRXaUF',
    description: 'Clinician Max plan feature on the pricing plans modal',
  },
  earlyAccessAdvancedAgenticFeatures: {
    defaultMessage: 'Early access to advanced agentic features',
    id: 's5mGp5gpma',
    description: 'Clinician Max plan feature on the pricing plans modal',
  },
  shareSessionsAndTemplates: {
    defaultMessage: 'Share sessions and templates across your team',
    id: '4HgXkzbQOw',
    description: 'Practice plan feature on the pricing plans modal',
  },
  addAssistantUsers: {
    defaultMessage: 'Add assistant users at no extra cost',
    id: 'cN1qeX7Kij',
    description: 'Practice plan feature on the pricing plans modal',
  },
  createEvidenceLibraries: {
    defaultMessage: 'Create personal and team Evidence libraries',
    id: 'sL0Xh1B+rK',
    description: 'Practice plan feature on the pricing plans modal',
  },
});

/**
 * Feature bullets follow the upgrade-copy sheet for the viewer's current tier.
 * Sheets cells marked "-" have no variant, so those viewers keep the Free list
 * on Clinician and the Clinician list on Practice.
 */
export function usePricingPlansModalFeatures(currentTier?: SubscriptionTier): {
  clinicianFeatures: PricingPlanCardFeature[];
  clinicianFeaturesTitle: MessageDescriptor;
  clinicianMaxFeatures: PricingPlanCardFeature[];
  practiceFeatures: PricingPlanCardFeature[];
  practiceFeaturesTitle: MessageDescriptor;
  /** Max seat of the Practice column: the Clinician Max bullets over Practice. */
  practiceMaxFeatures: PricingPlanCardFeature[];
  practiceMaxFeaturesTitle: MessageDescriptor;
} {
  const { formatMessage } = useIntl();
  const feature = (
    descriptor: (typeof messages)[keyof typeof messages]
  ): PricingPlanCardFeature => ({
    icon: Check,
    description: formatMessage(descriptor),
  });

  const isScribePlus = currentTier === SubscriptionTier.PRO;
  const isScribeTeam = currentTier === SubscriptionTier.PRO_PLUS;
  const maxFeatures = [
    feature(messages.maxAgenticUsage),
    feature(messages.designedForRegularAgenticUse),
    feature(messages.earlyAccessAdvancedAgenticFeatures),
  ];

  return {
    clinicianFeatures: isScribePlus
      ? [
          feature(messages.evidenceAnswersInSessions),
          feature(messages.choosePreferredEvidenceSources),
          feature(messages.tryAgenticWorkflows),
        ]
      : [
          feature(messages.customScribeTemplates),
          feature(messages.evidenceAnswersInSessions),
          feature(messages.tryAgenticWorkflows),
        ],
    clinicianFeaturesTitle: isScribePlus
      ? pricingPlansModalMessages.featuresEverythingInScribePlus
      : pricingPlansModalMessages.featuresEverythingInFree,
    clinicianMaxFeatures: maxFeatures,
    practiceFeatures: isScribeTeam
      ? [
          feature(messages.evidenceAnswersInSessions),
          feature(messages.choosePreferredEvidenceSources),
          feature(messages.createEvidenceLibraries),
          feature(messages.tryAgenticWorkflows),
        ]
      : [
          feature(messages.shareSessionsAndTemplates),
          feature(messages.addAssistantUsers),
          feature(messages.createEvidenceLibraries),
        ],
    practiceFeaturesTitle: isScribeTeam
      ? pricingPlansModalMessages.featuresEverythingInScribeTeam
      : pricingPlansModalMessages.featuresEverythingInClinician,
    practiceMaxFeatures: maxFeatures,
    practiceMaxFeaturesTitle: pricingPlansModalMessages.featuresEverythingInPractice,
  };
}
